/**
 * src/components/DownloadButton.jsx — a button that downloads a private file.
 *
 * Signed consent PDFs are no longer public URLs (LAUNCH.md Phase 3), so a plain
 * `<a href>` can't fetch them: the request has to go through lib/api.js's
 * downloadFile() with the session. This wraps that with the busy state and a
 * visible error, so the calendar drawer, the consent tracker and the signing
 * page all behave the same way.
 */
import { useState } from 'react';

import { downloadFile } from '../lib/api.js';

/**
 * @param {{ path: string, filename: string, className?: string,
 *   children: React.ReactNode }} props `path` is an API path such as a
 *   consent form's `pdfPath`.
 * @returns {JSX.Element}
 */
export default function DownloadButton({ path, filename, className = 'btn btn-sm', children }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadFile(path, filename);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className={className} disabled={busy} onClick={download}>
        {busy ? 'Downloading…' : children}
      </button>
      {error ? (
        <span className="small" role="alert" style={{ color: 'var(--danger)' }}>
          {error}
        </span>
      ) : null}
    </>
  );
}
